import type { Dayjs } from "dayjs";
import { generateCalendarDays } from "../utils/calendarHelpers";
import { StudyTrackerDay } from "./StudyTrackerDay";
import type { DayStats } from "../utils/studyTrackerHelpers";
import { getStatsForDate } from "../utils/studyTrackerHelpers";

interface StudyTrackerCalendarProps {
    month: Dayjs;
    daysStats: DayStats[];
    onDayClick: (date: Dayjs, dayStats: DayStats | null) => void;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function StudyTrackerCalendar({ month, daysStats, onDayClick }: StudyTrackerCalendarProps) {
    const days = generateCalendarDays(month);

    return (
        <div className="p-2 w-full">
            {/* Month header */}
            <div className="mb-6 text-center">
                <h2 className="font-bold text-slate-900 text-3xl">{month.format("MMMM YYYY")}</h2>
            </div>

            {/* Weekday headers */}
            <div className="gap-2 grid grid-cols-7 mb-3">
                {WEEKDAYS.map((day) => (
                    <div key={day} className="py-2 font-semibold text-slate-600 text-sm text-center">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="gap-4 grid grid-cols-7">
                {days.map((day, index) => {
                    const dayStats = getStatsForDate(day.date, daysStats);

                    return (
                        <StudyTrackerDay
                            key={index}
                            day={day}
                            dayStats={dayStats}
                            onClick={() => onDayClick(day.date, dayStats)}
                        />
                    );
                })}
            </div>

            {/* Legend */}
            <div className="mt-6 pt-4 border-slate-200 border-t">
                <div className="flex flex-wrap justify-center gap-4 text-xs">
                    <div className="flex items-center gap-2">
                        <div className="bg-green-50 border border-green-200 rounded w-4 h-4" />
                        <span className="text-slate-600">All completed</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="bg-amber-50 border border-amber-200 rounded w-4 h-4" />
                        <span className="text-slate-600">Partial</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="bg-red-50 border border-red-200 rounded w-4 h-4" />
                        <span className="text-slate-600">None completed</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
